import {AdvertiserMediaModel} from './advertiser-media.model';
import {Action, State, StateContext} from '@ngxs/store';
import {ViewType} from '@shared/enums/view-type.enum';
import {Injectable} from '@angular/core';
import {
  AdvertiserAddMedia,
  AdvertiserCleanMediaStore, AdvertiserDeleteMedia, AdvertiserGetMediaByType,
  AdvertiserSearchMedia, AdvertiserSetMediaPage,
  AdvertiserSetMediaView, AdvertiserSortMedia,
  GetAdvertiserMedia
} from './advertiser-media.actions';
import {Observable, of} from 'rxjs';
import {IResponse} from '@interfaces/response';
import {IMediaItem} from '@interfaces/media';
import {MediaService} from '@services/media/media.service';
import {switchMap, tap} from 'rxjs/operators';

export const initialMediaStateModel: AdvertiserMediaModel = {
  itemsPerPage: 8,
  search: '',
  media: null,
  totalMediaNum: 0,
  currentPage: 1,
  viewType: ViewType.card,
  mediaType: null,
  order: '-created_at',
  loader: false,
};

@State<AdvertiserMediaModel>({
  name: 'advertiserMediaState',
  defaults: initialMediaStateModel
})

@Injectable()
export class AdvertiserMediaState {
  constructor(private mediaService: MediaService) {
  }

  @Action(GetAdvertiserMedia, {cancelUncompleted: true})
  getAdvertiserMedia(ctx: StateContext<AdvertiserMediaModel>): Observable<IResponse<IMediaItem>> {
    const limit = ctx.getState().itemsPerPage;
    const skip = limit * (ctx.getState().currentPage - 1);
    const search = ctx.getState().search;
    const mediaType = ctx.getState().mediaType;
    const order = ctx.getState().order;

    ctx.patchState({loader: true});
    return this.mediaService.getAllMedia({
      limit,
      skip,
      order,
      search,
      media_type: mediaType || '',
    }).pipe(
      tap((media) => {
        ctx.setState({...ctx.getState(), mediaType, media: media.rows, totalMediaNum: media.total, loader: false});
      })
    );
  }

  @Action(AdvertiserSetMediaPage)
  advertiserSetMediaPage(ctx: StateContext<AdvertiserMediaModel>, {page}: AdvertiserSetMediaPage): Observable<any> {
    if (ctx.getState().currentPage === page) return of(null);
    ctx.patchState({currentPage: page});
    return ctx.dispatch(new GetAdvertiserMedia());
  }

  @Action(AdvertiserSearchMedia)
  advertiserSearchMedia(ctx: StateContext<AdvertiserMediaModel>, {search}: AdvertiserSearchMedia): Observable<any> {
    ctx.patchState({search, currentPage: 1});
    return ctx.dispatch(new GetAdvertiserMedia());
  }

  @Action(AdvertiserGetMediaByType)
  advertiserGetMediaByType(ctx: StateContext<AdvertiserMediaModel>, {type}: AdvertiserGetMediaByType): Observable<any> {
    ctx.patchState({mediaType: type, currentPage: 1, search: '', order: '-created_at'});
    return ctx.dispatch(new GetAdvertiserMedia());
  }

  @Action(AdvertiserSetMediaView)
  advertiserSetMediaView(ctx: StateContext<AdvertiserMediaModel>, {type}: AdvertiserSetMediaView): void {
    ctx.patchState({viewType: type});
  }

  @Action(AdvertiserCleanMediaStore)
  advertiserCleanMediaStore(ctx: StateContext<AdvertiserMediaModel>): void {
    ctx.setState({
      itemsPerPage: 8,
      search: '',
      media: null,
      totalMediaNum: 0,
      currentPage: 1,
      viewType: ViewType.card,
      mediaType: null,
      order: '-created_at',
      loader: false
    });
  }

  @Action(AdvertiserAddMedia)
  advertiserAddMedia(ctx: StateContext<AdvertiserMediaModel>, {file}: AdvertiserAddMedia): Observable<any> {
    return this.mediaService.uploadFile(file)
      .pipe(
        switchMap(() => ctx.dispatch(new GetAdvertiserMedia()))
      );
  }

  @Action(AdvertiserDeleteMedia)
  advertiserDeleteMedia(ctx: StateContext<AdvertiserMediaModel>, {ids}: AdvertiserDeleteMedia): Observable<any> {
    return this.mediaService.removeMedias(ids)
      .pipe(
        tap(() => {
          const state = ctx.getState();
          const currentPage = this.getCurrentPageAfterDelete(
            state.currentPage,
            state.itemsPerPage,
            state.totalMediaNum,
            ids.length
          );

          ctx.patchState({currentPage});
        }),
        switchMap(() => ctx.dispatch(new GetAdvertiserMedia()))
      );
  }

  @Action(AdvertiserSortMedia)
  advertiserSortMedia(ctx: StateContext<AdvertiserMediaModel>, {payload}: AdvertiserSortMedia): Observable<any> {
    ctx.patchState({order: payload.sort});
    return ctx.dispatch(new GetAdvertiserMedia());
  }

  private getCurrentPageAfterDelete(currentPage: number, perPage: number, total = 0, deletedCount = 1): number {
    const totalAfterDelete = Math.max(total - deletedCount, 0);
    const lastAvailablePage = Math.max(Math.ceil(totalAfterDelete / perPage), 1);

    return Math.min(currentPage, lastAvailablePage);
  }
}
